/**
 * Owner: Masa Bayu
 * File: qx-draft-image-style.ts
 * Date:Time: 2026-10-16 01:14
 * Description: Word-like picture frame/shadow/smoke · session DOM.
 */

export type DraftFigureFrame = 'none' | 'thin' | 'thick' | 'soft';
export type DraftFigureShadow = 'none' | 'soft' | 'offset' | 'smoke';

/** Combined picture look · Word Picture Styles–style presets. */
export type DraftFigurePreset =
  | 'none'
  | 'simple'
  | 'soft-frame'
  | 'drop-shadow'
  | 'framed-shadow'
  | 'thick'
  | 'smoke';

export type DraftFigurePicture = {
  readonly frame: DraftFigureFrame;
  readonly shadow: DraftFigureShadow;
};

const FIGURE_CLASS = 'qxw-draft-figure';
const SELECTED_CLASS = 'qxw-draft-figure--selected';
const FRAME_ATTR = 'data-qx-frame';
const SHADOW_ATTR = 'data-qx-shadow';

const FRAME_VALUES: readonly DraftFigureFrame[] = ['none', 'thin', 'thick', 'soft'];
const SHADOW_VALUES: readonly DraftFigureShadow[] = ['none', 'soft', 'offset', 'smoke'];

const PRESETS: Readonly<Record<DraftFigurePreset, DraftFigurePicture>> = {
  none: { frame: 'none', shadow: 'none' },
  simple: { frame: 'thin', shadow: 'none' },
  'soft-frame': { frame: 'soft', shadow: 'none' },
  'drop-shadow': { frame: 'none', shadow: 'soft' },
  'framed-shadow': { frame: 'thin', shadow: 'soft' },
  thick: { frame: 'thick', shadow: 'none' },
  smoke: { frame: 'none', shadow: 'smoke' },
};

export function parseDraftFigureFrame(raw: string | null | undefined): DraftFigureFrame | null {
  if (raw === 'none' || raw === 'thin' || raw === 'thick' || raw === 'soft') return raw;
  return null;
}

export function parseDraftFigureShadow(raw: string | null | undefined): DraftFigureShadow | null {
  if (raw === 'none' || raw === 'soft' || raw === 'offset' || raw === 'smoke') return raw;
  return null;
}

export function parseDraftFigurePreset(raw: string | null | undefined): DraftFigurePreset | null {
  if (
    raw === 'none' ||
    raw === 'simple' ||
    raw === 'soft-frame' ||
    raw === 'drop-shadow' ||
    raw === 'framed-shadow' ||
    raw === 'thick' ||
    raw === 'smoke'
  ) {
    return raw;
  }
  return null;
}

export function selectedDraftFigure(): HTMLElement | null {
  const editor = document.querySelector('.qxw-page__editor');
  if (!(editor instanceof HTMLElement)) return null;
  const selected = editor.querySelector(`.${SELECTED_CLASS}`);
  if (selected instanceof HTMLElement && selected.classList.contains(FIGURE_CLASS)) {
    return selected;
  }
  const last = editor.querySelector(`.${FIGURE_CLASS}`);
  return last instanceof HTMLElement ? last : null;
}

function selectFigure(figure: HTMLElement): void {
  const editor = figure.closest('.qxw-page__editor');
  if (!(editor instanceof HTMLElement)) return;
  editor.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));
  figure.classList.add(SELECTED_CLASS);
}

export function draftFigurePictureFromDom(figure: HTMLElement): DraftFigurePicture {
  return {
    frame: parseDraftFigureFrame(figure.getAttribute(FRAME_ATTR)) ?? 'none',
    shadow: parseDraftFigureShadow(figure.getAttribute(SHADOW_ATTR)) ?? 'none',
  };
}

/** Apply frame / shadow classes from data attrs (idempotent). */
export function normalizeDraftFigureStyle(figure: HTMLElement): void {
  const picture = draftFigurePictureFromDom(figure);
  applyDraftFigurePicture(figure, picture);
}

export function applyDraftFigureFrame(figure: HTMLElement, frame: DraftFigureFrame): void {
  applyDraftFigurePicture(figure, {
    frame,
    shadow: draftFigurePictureFromDom(figure).shadow,
  });
}

export function applyDraftFigureShadow(figure: HTMLElement, shadow: DraftFigureShadow): void {
  applyDraftFigurePicture(figure, {
    frame: draftFigurePictureFromDom(figure).frame,
    shadow,
  });
}

export function applyDraftFigurePicture(figure: HTMLElement, picture: DraftFigurePicture): void {
  figure.setAttribute(FRAME_ATTR, picture.frame);
  figure.setAttribute(SHADOW_ATTR, picture.shadow);
  for (const value of FRAME_VALUES) {
    figure.classList.toggle(`qxw-draft-figure--frame-${value}`, picture.frame === value);
  }
  for (const value of SHADOW_VALUES) {
    figure.classList.toggle(`qxw-draft-figure--shadow-${value}`, picture.shadow === value);
  }
}

export function applyDraftFigurePreset(figure: HTMLElement, preset: DraftFigurePreset): void {
  applyDraftFigurePicture(figure, PRESETS[preset]);
}

export function runDraftFigureFrame(frame: DraftFigureFrame): boolean {
  const figure = selectedDraftFigure();
  if (!figure) return false;
  applyDraftFigureFrame(figure, frame);
  selectFigure(figure);
  return true;
}

export function runDraftFigureShadow(shadow: DraftFigureShadow): boolean {
  const figure = selectedDraftFigure();
  if (!figure) return false;
  applyDraftFigureShadow(figure, shadow);
  selectFigure(figure);
  return true;
}

export function runDraftFigurePreset(preset: DraftFigurePreset): boolean {
  const figure = selectedDraftFigure();
  if (!figure) return false;
  applyDraftFigurePreset(figure, preset);
  selectFigure(figure);
  return true;
}

/** Class tokens for hydrate HTML. */
export function draftFigureStyleClassNames(picture: DraftFigurePicture): string {
  return `qxw-draft-figure--frame-${picture.frame} qxw-draft-figure--shadow-${picture.shadow}`;
}

/** data-qx-frame / data-qx-shadow attribute fragment. */
export function draftFigureStyleDataAttrs(picture: DraftFigurePicture): string {
  return ` ${FRAME_ATTR}="${picture.frame}" ${SHADOW_ATTR}="${picture.shadow}"`;
}
